/**
 * ContentStudio v2 — Template-first composer powered by the Enable Healthcare
 * Design System catalog (21 social templates) + a block-based Email composer.
 *
 * Layout (1400px max):
 *   • Sticky LEFT Tweaks panel — 220px — always visible, never blocking
 *   • Brief column — 260px
 *   • Canvas — flex 1, dynamically resizes per active variant size
 *   • Variants column — 240px (channels + alt-templates)
 *
 * Each variant in the right column carries a `size` (square/portrait/story/
 * wide/yt) — clicking switches the canvas to that variant's native template
 * AND its native aspect ratio. The email variant swaps in a block composer.
 *
 * Mocked end-to-end. Schedule + Publish wire into ScheduleModal.
 */
import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Sparkles, Save, Rocket, RotateCcw, CheckCircle2, ImageIcon, FolderOpen,
  Linkedin, Instagram, Twitter, Facebook, Youtube, Mail as MailIcon, FileText, Loader2, Calendar as CalIcon,
} from "lucide-react";
import MarketingShell from "@/components/MarketingShell";
import ScheduleModal from "@/components/marketing/ScheduleModal";
import { toast } from "sonner";
import "@/marketing/design-system/styles.css";
import { CATALOG, SIZES, TemplateEdit, Stage, getTemplate } from "@/marketing/design-system";
import EmailComposer from "@/marketing/design-system/EmailComposer";
import {
  saveTemplate as saveStoreTemplate,
  getTemplate as getStoreTemplate,
  refreshTemplates as refreshStoreTemplates,
} from "@/lib/marketingTemplatesStore";
import usePrompt from "@/hooks/usePrompt";

const CHANNEL_VARIANTS = [
  { id: "li-feed", label: "LinkedIn feed", icon: Linkedin, color: "#0077B5", size: "square", templateId: "sq-announce", note: "1080×1080" },
  { id: "li-link", label: "LinkedIn link card", icon: Linkedin, color: "#0077B5", size: "wide", templateId: "wd-ehr", note: "1200×628" },
  { id: "ig-feed", label: "Instagram feed", icon: Instagram, color: "#E4405F", size: "portrait", templateId: "po-ehr", note: "1080×1350" },
  { id: "ig-story", label: "Instagram story", icon: Instagram, color: "#E4405F", size: "story", templateId: "st-headline", note: "1080×1920" },
  { id: "x-card", label: "X / Twitter card", icon: Twitter, color: "#0f1419", size: "wide", templateId: "wd-stat", note: "1200×628" },
  { id: "fb-feed", label: "Facebook feed", icon: Facebook, color: "#1877F2", size: "square", templateId: "sq-stat", note: "1080×1080" },
  { id: "yt-banner", label: "YouTube banner", icon: Youtube, color: "#FF0000", size: "yt", templateId: "yt-banner", note: "1920×1080" },
  { id: "email", label: "Email", icon: MailIcon, color: "#2E63B8", size: "email", templateId: null, note: "Block composer" },
  { id: "blog", label: "Blog hero", icon: FileText, color: "#1B365D", size: "wide", templateId: "wd-rpm", note: "1200×628" },
];

export default function ContentStudio() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const promptFn = usePrompt();
  const editingId = searchParams.get("template");
  const [loadedFromStore, setLoadedFromStore] = useState(null);

  const [activeVariantId, setActiveVariantId] = useState("li-feed");
  const [activeTemplateId, setActiveTemplateId] = useState("sq-announce");
  const [bgVariant, setBgVariant] = useState("brand");
  const [eyebrowCase, setEyebrowCase] = useState("UPPERCASE");
  const [ctaStyle, setCtaStyle] = useState("pill");
  const [density, setDensity] = useState(1.0);
  const [brief, setBrief] = useState({
    topic: "",
    facts: "",
    tone: "thought-leadership",
    audience: "",
  });
  // Email composer state (controlled — needed for save-to-template)
  const [emailDraft, setEmailDraft] = useState({
    subject: "",
    preview: "",
    fromName: "Enable Healthcare",
    fromEmail: "hello@enablehealthcare.com",
    blocks: [],
  });
  const [generating, setGenerating] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [savedTemplateId, setSavedTemplateId] = useState(null);

  // Load template from store when ?template=<id> is present.
  useEffect(() => {
    if (!editingId || loadedFromStore === editingId) return;
    let cancelled = false;
    (async () => {
      // Ensure cache is hydrated from the server before reading (the
      // sync `getStoreTemplate` reads from the in-memory cache, which
      // is empty on a hard refresh).
      await refreshStoreTemplates();
      if (cancelled) return;
      const tpl = getStoreTemplate(editingId);
      if (!tpl) return;
      setSavedTemplateId(tpl.id);
      setLoadedFromStore(tpl.id);
      if (tpl.kind === "email") {
        setActiveVariantId("email");
        setEmailDraft({
          subject: tpl.subject || "",
          preview: tpl.preview || "",
          fromName: tpl.fromName || "Enable Healthcare",
          fromEmail: tpl.fromEmail || "hello@enablehealthcare.com",
          blocks: tpl.blocks || [],
        });
      } else if (tpl.kind === "social") {
        if (tpl.variantId) setActiveVariantId(tpl.variantId);
        if (tpl.templateId) setActiveTemplateId(tpl.templateId);
        if (tpl.tweaks) {
          if (tpl.tweaks.bgVariant) setBgVariant(tpl.tweaks.bgVariant);
          if (tpl.tweaks.eyebrowCase) setEyebrowCase(tpl.tweaks.eyebrowCase);
          if (tpl.tweaks.ctaStyle) setCtaStyle(tpl.tweaks.ctaStyle);
          if (typeof tpl.tweaks.density === "number") setDensity(tpl.tweaks.density);
        }
        if (tpl.brief) setBrief({ ...{ topic: "", facts: "", tone: "thought-leadership", audience: "" }, ...tpl.brief });
      }
      toast.success(`Loaded "${tpl.name}"`);
    })();
    return () => { cancelled = true; };
  }, [editingId, loadedFromStore]);

  const activeVariant = CHANNEL_VARIANTS.find((v) => v.id === activeVariantId);
  const isEmail = activeVariant?.size === "email";

  // Pick the active template (manual or from variant default)
  const active = useMemo(() => {
    if (isEmail) return null;
    const id = activeTemplateId || activeVariant?.templateId;
    return getTemplate(id) || CATALOG[0];
  }, [activeTemplateId, activeVariant, isEmail]);

  // Alternative templates filtered to the active variant's size (so swapping
  // stays within the right canvas dimensions)
  const altTemplates = useMemo(() => {
    if (isEmail || !activeVariant) return [];
    return CATALOG.filter((t) => t.size === activeVariant.size);
  }, [activeVariant, isEmail]);

  const generate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      toast.success("Content Agent generated 9 variants from the brief");
    }, 1400);
  };

  const onPickVariant = (v) => {
    setActiveVariantId(v.id);
    if (v.templateId) setActiveTemplateId(v.templateId);
  };

  const onSaveTemplate = async () => {
    const defaultName = isEmail
      ? (emailDraft.subject || "Untitled email").slice(0, 60)
      : `${active?.name || "Untitled"} · ${SIZES[activeVariant?.size]?.label || ""}`.trim();
    const name = await promptFn({
      title: savedTemplateId ? "Update template name" : "Name this template",
      body: "Templates appear in the Template Library and can be edited any time.",
      defaultValue: defaultName,
      placeholder: "Template name",
      submitLabel: savedTemplateId ? "Update" : "Save template",
    });
    if (!name || !name.trim()) return;
    const base = {
      id: savedTemplateId || undefined,
      name: name.trim(),
      kind: isEmail ? "email" : "social",
    };
    const record = isEmail
      ? { ...base, ...emailDraft }
      : {
          ...base,
          templateId: active?.id,
          size: activeVariant?.size,
          variantId: activeVariantId,
          tweaks: { bgVariant, eyebrowCase, ctaStyle, density },
          brief,
        };
    const saved = await saveStoreTemplate(record);
    setSavedTemplateId(saved.id);
    const sp = new URLSearchParams(searchParams);
    sp.set("template", saved.id);
    setSearchParams(sp, { replace: true });
    toast.success(`Saved "${saved.name}" to Template Library`);
  };

  const headerActions = (
    <>
      <span className="tag tag-green" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <CheckCircle2 style={{ width: 11, height: 11 }} /> Brand-safe
      </span>
      <button className="btn" onClick={() => navigate("/marketing/create/templates")} data-testid="open-template-library">
        <FolderOpen style={{ width: 13, height: 13 }} /> Library
      </button>
      <button className="btn" onClick={onSaveTemplate} data-testid="save-template">
        <Save style={{ width: 13, height: 13 }} /> {savedTemplateId ? "Update" : "Save as template"}
      </button>
      <button className="btn" onClick={() => setScheduleOpen(true)} data-testid="open-schedule"><CalIcon style={{ width: 13, height: 13 }} /> Schedule</button>
      <button className="btn btn-primary" onClick={() => toast.success("Queued in Approvals")}>
        <Rocket style={{ width: 13, height: 13 }} /> Publish
      </button>
    </>
  );

  return (
    <MarketingShell title="Content Studio" subtitle="Template-first composer · Enable Healthcare Design System" actions={headerActions}>
      <div className="cs-v2-grid" data-testid="content-studio">
        {/* STICKY LEFT — Tweaks */}
        <aside className="cs-v2-tweaks" data-testid="cs-tweaks">
          <h3 className="cs-col-title">Tweaks</h3>

          <label className="cs-label">Background</label>
          <div className="mkt-segmented" style={{ flexWrap: "wrap", padding: 3 }}>
            {[{ k: "brand", l: "Brand" }, { k: "soft", l: "Soft" }, { k: "light", l: "Light" }, { k: "white", l: "White" }, { k: "deep", l: "Deep" }].map((b) => (
              <button key={b.k} onClick={() => setBgVariant(b.k)} className={bgVariant === b.k ? "active" : ""}>{b.l}</button>
            ))}
          </div>

          <label className="cs-label">Eyebrow case</label>
          <select className="input" value={eyebrowCase} onChange={(e) => setEyebrowCase(e.target.value)}>
            <option>UPPERCASE</option>
            <option>Title Case</option>
            <option>sentence case</option>
          </select>

          <label className="cs-label">CTA style</label>
          <div className="mkt-segmented">
            {["pill", "square", "underline"].map((c) => (
              <button key={c} onClick={() => setCtaStyle(c)} className={ctaStyle === c ? "active" : ""}>{c}</button>
            ))}
          </div>

          <label className="cs-label">Density</label>
          <input type="range" min={0.85} max={1.2} step={0.05} value={density} onChange={(e) => setDensity(parseFloat(e.target.value))} style={{ width: "100%" }} />
          <div className="num" style={{ fontSize: 10.5, color: "var(--text-tertiary)", textAlign: "right" }}>{density.toFixed(2)}×</div>

          <label className="cs-label">Logo variant</label>
          <select className="input">
            <option>Lockup · color-adaptive</option>
            <option>Wordmark only</option>
            <option>Emblem only</option>
            <option>No logo</option>
          </select>

          <div className="cs-tweaks-hint">
            <Sparkles style={{ width: 11, height: 11 }} /> Tweaks apply to the active template only.
          </div>
        </aside>

        {/* BRIEF */}
        <aside className="cs-v2-brief" data-testid="cs-brief">
          <h3 className="cs-col-title">Brief</h3>
          <label className="cs-label">Topic</label>
          <textarea className="input cs-textarea" rows={3} value={brief.topic} onChange={(e) => setBrief({ ...brief, topic: e.target.value })} data-testid="brief-topic" />
          <label className="cs-label">Must-include facts / stats</label>
          <textarea className="input cs-textarea" rows={2} value={brief.facts} onChange={(e) => setBrief({ ...brief, facts: e.target.value })} />
          <label className="cs-label">Tone</label>
          <select className="input" value={brief.tone} onChange={(e) => setBrief({ ...brief, tone: e.target.value })}>
            <option value="thought-leadership">Thought leadership</option>
            <option value="announcement">Announcement</option>
            <option value="educational">Educational</option>
            <option value="promotional">Promotional</option>
            <option value="event">Event</option>
          </select>
          <label className="cs-label">Audience</label>
          <input className="input" value={brief.audience} onChange={(e) => setBrief({ ...brief, audience: e.target.value })} />
          <button onClick={generate} className="btn btn-primary cs-generate" disabled={generating} data-testid="cs-generate">
            {generating
              ? <><Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> Generating…</>
              : <><Sparkles style={{ width: 13, height: 13 }} /> Regenerate</>}
          </button>
        </aside>

        {/* CANVAS */}
        <section className="cs-v2-canvas eh-ds-scope" data-testid="cs-canvas"
          data-size={isEmail ? "email" : activeVariant?.size}
          data-tweak-bg={bgVariant}
          data-tweak-eyebrow={eyebrowCase.replace(/\s+/g, "-").toLowerCase()}
          data-tweak-cta={ctaStyle}
          style={{ "--cs-density": density }}
        >
          <div className="cs-canvas-toolbar">
            <span className="eyebrow">Canvas · {isEmail ? "Email" : `${SIZES[activeVariant?.size]?.label} ${SIZES[activeVariant?.size]?.w}×${SIZES[activeVariant?.size]?.h}`}</span>
            <div style={{ flex: 1 }} />
            {!isEmail && <span className="tag tag-gray">{active?.name}</span>}
            <button className="btn" style={{ height: 28, padding: "0 9px" }} title="Reset"><RotateCcw style={{ width: 13, height: 13 }} /></button>
          </div>
          <div className="cs-v2-stage">
            {isEmail ? (
              <EmailComposer value={emailDraft} onChange={setEmailDraft} />
            ) : (
              <TemplateEdit id={active?.id || "blank"}>
                <Stage size={activeVariant.size}>
                  {active?.render()}
                </Stage>
              </TemplateEdit>
            )}
          </div>
          <div className="cs-hint">
            <Sparkles style={{ width: 11, height: 11 }} /> Click any text in the canvas to edit inline · click any image to replace.
          </div>
        </section>

        {/* VARIANTS */}
        <aside className="cs-v2-variants" data-testid="cs-variants">
          <h3 className="cs-col-title">Channel variants</h3>
          <div className="cs-variant-list">
            {CHANNEL_VARIANTS.map((v) => {
              const Icon = v.icon;
              return (
                <button key={v.id} onClick={() => onPickVariant(v)}
                  className={`cs-variant ${activeVariantId === v.id ? "is-active" : ""}`}
                  data-testid={`variant-${v.id}`}>
                  <Icon style={{ width: 13, height: 13, color: v.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 540 }}>{v.label}</div>
                    <div className="num" style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{v.note}</div>
                  </div>
                </button>
              );
            })}
          </div>
          {!isEmail && altTemplates.length > 0 && (
            <>
              <h3 className="cs-col-title" style={{ marginTop: 16 }}>Alt templates · {SIZES[activeVariant.size]?.label}</h3>
              <div className="cs-variant-list">
                {altTemplates.map((t) => (
                  <button key={t.id} onClick={() => setActiveTemplateId(t.id)}
                    className={`cs-variant ${activeTemplateId === t.id ? "is-active" : ""}`}
                    data-testid={`tpl-${t.id}`}>
                    <ImageIcon style={{ width: 13, height: 13, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 540 }}>{t.name}</div>
                      <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{t.layout}</div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>

      <ScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} item={{ title: active?.name || "Email draft" }} />
    </MarketingShell>
  );
}
